import type { ThunkAction, ThunkDispatch, UnknownAction } from '@reduxjs/toolkit';
import { authActions } from '../slices/authSlice.ts';
import type { CommonState } from '../selectors/commonSelectors.ts';
import * as authService from '../../services/auth/authService.ts';
import type { LoginCredentials } from '../../services/auth/authService.ts';
import { setAccessToken } from '../../services/auth/token.ts';
import { mockEntraSession, mockVendorSsoSession } from '../../services/auth/mockEntra.ts';

export type CommonDispatch = ThunkDispatch<CommonState, unknown, UnknownAction>;
export type CommonThunk<Result = void> = ThunkAction<Result, CommonState, unknown, UnknownAction>;

export const loginWithCredentials = (credentials: LoginCredentials): CommonThunk<Promise<void>> => async (dispatch) => {
  dispatch(authActions.setLoading(true));
  try {
    dispatch(authActions.setSession(await authService.login(credentials)));
  } finally {
    dispatch(authActions.setLoading(false));
  }
};

export const logoutSession = (): CommonThunk<Promise<void>> => async (dispatch, getState) => {
  try {
    if (getState().auth.accessToken) await authService.logout();
  } catch {
    return;
  } finally {
    setAccessToken(null);
    dispatch(authActions.clear());
  }
};

export const initializeSession = (): CommonThunk<Promise<void>> => async (dispatch, getState) => {
  const token = getState().auth.accessToken;
  try {
    if (token) {
      const user = await authService.getCurrentUser();
      const previous = getState().auth.user;
      // A Microsoft sign-in shows the real account while the session belongs to a supplier record; keep that display identity.
      const display = previous?.sub === user.sub ? { name: previous.name, email: previous.email, preferredUsername: previous.preferredUsername } : {};
      if (getState().auth.accessToken === token) dispatch(authActions.setUser({ ...user, ...display }));
    }
  } catch {
    if (getState().auth.accessToken === token) dispatch(authActions.clear());
  } finally {
    if (getState().auth.accessToken === token || !getState().auth.accessToken) dispatch(authActions.setInitialized(true));
  }
};

export const initializeMockEntraSession = (role: string = 'Administrator'): CommonThunk<Promise<void>> => async (dispatch) => {
  const session = mockEntraSession(role);
  dispatch(authActions.clear());
  dispatch(authActions.setSession(session));
};

export const selectMockTestRole = (role: string): CommonThunk<Promise<void>> => async (dispatch) => {
  await dispatch(initializeMockEntraSession(role));
};

export const signInWithMockVendorSso = (userId: string, identity?: { name?: string; email?: string }): CommonThunk<Promise<void>> => async (dispatch) => {
  const session = mockVendorSsoSession(userId, identity);
  dispatch(authActions.clear());
  dispatch(authActions.setSession(session));
};